import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { OverviewAdminKpisRepository } from '../../application/OverviewAdminKpisRepository'
import type { OverviewAdminKpis } from '../../domain/overviewAdminKpis'

const cents = z.coerce.number().int()

const resultSchema = z.object({
  season: z.string().nullable(),
  seasons: z.array(z.string()),
  computed_at: z.string(),
  invoices: z
    .object({
      invoiced_cents: cents,
      to_collect_cents: cents,
      overdue_cents: cents,
      open_invoices_count: cents,
      overdue_invoices_count: cents,
    })
    .nullable(),
  payments: z.object({ collected_cents: cents, collected_this_month_cents: cents }).nullable(),
  contracts: z
    .object({
      clients_count: cents,
      signed_count: cents,
      signed_value_cents: cents,
      active_count: cents,
      awaiting_signature_count: cents,
      awaiting_signature_value_cents: cents,
      vip_count: cents,
      vip_value_cents: cents,
    })
    .nullable(),
  overdue_invoices: z
    .array(
      z.object({
        id: z.string(),
        number: z.string().nullable(),
        due_on: z.string(),
        balance_cents: cents,
        contract_number: z.string().nullable(),
        client_name: z.string(),
      }),
    )
    .nullable(),
})

export function parseOverviewAdminKpis(data: unknown): OverviewAdminKpis {
  const parsed = resultSchema.safeParse(data)
  if (!parsed.success) throw new Error('OVERVIEW_KPIS_RESPONSE_INVALID')
  const raw = parsed.data
  return {
    season: raw.season,
    seasons: raw.seasons,
    computedAt: raw.computed_at,
    invoices: raw.invoices && {
      invoicedCents: raw.invoices.invoiced_cents,
      toCollectCents: raw.invoices.to_collect_cents,
      overdueCents: raw.invoices.overdue_cents,
      openInvoicesCount: raw.invoices.open_invoices_count,
      overdueInvoicesCount: raw.invoices.overdue_invoices_count,
    },
    payments: raw.payments && {
      collectedCents: raw.payments.collected_cents,
      collectedThisMonthCents: raw.payments.collected_this_month_cents,
    },
    contracts: raw.contracts && {
      clientsCount: raw.contracts.clients_count,
      signedCount: raw.contracts.signed_count,
      signedValueCents: raw.contracts.signed_value_cents,
      activeCount: raw.contracts.active_count,
      awaitingSignatureCount: raw.contracts.awaiting_signature_count,
      awaitingSignatureValueCents: raw.contracts.awaiting_signature_value_cents,
      vipCount: raw.contracts.vip_count,
      vipValueCents: raw.contracts.vip_value_cents,
    },
    overdueInvoices:
      raw.overdue_invoices?.map((row) => ({
        id: row.id,
        number: row.number ?? '',
        dueOn: row.due_on,
        balanceCents: row.balance_cents,
        contractNumber: row.contract_number,
        clientName: row.client_name,
      })) ?? null,
  }
}

export function createSupabaseOverviewAdminKpisRepository(
  client: AppSupabaseClient,
): OverviewAdminKpisRepository {
  return {
    async load(season) {
      const rpcClient = client as unknown as SupabaseClient
      const response = await rpcClient.rpc('overview_admin_kpis_v1', { p_season: season })
      if (response.error) throw new Error(response.error.message || 'OVERVIEW_KPIS_FAILED')
      return parseOverviewAdminKpis(response.data)
    },
  }
}
